import { useState, useCallback, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TrendingUp, Plus, Trash2, Save } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";

interface Client {
  id: string;
  name: string;
}

// Product categories from Sales Overview
const PRODUCT_CATEGORIES = [
  "Wisdrop",
  "Contracts",
  "zevioo",
  "Brand Beat",
  "Qualitative",
  "Other Ad-Hoc",
] as const;

const MONTHS = [
  { value: 1, label: "January" },
  { value: 2, label: "February" },
  { value: 3, label: "March" },
  { value: 4, label: "April" },
  { value: 5, label: "May" },
  { value: 6, label: "June" },
  { value: 7, label: "July" },
  { value: 8, label: "August" },
  { value: 9, label: "September" },
  { value: 10, label: "October" },
  { value: 11, label: "November" },
  { value: 12, label: "December" },
] as const;

interface ExploitationRow {
  id: string;
  client_id: string;
  description: string | null;
  product_category: string | null;
  month: number | null;
  sales: number | null;
  probability: number | null;
  year: number;
  created_at: string;
  updated_at: string;
  created_by: string | null;
  isNew?: boolean;
  isDirty?: boolean;
}

export default function ClientsArchitecture() {
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState<number>(currentYear);
  const [editableRows, setEditableRows] = useState<ExploitationRow[]>([]);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Generate year options (current year + 5 previous years + 2 future years)
  const yearOptions = Array.from({ length: 8 }, (_, i) => currentYear + 2 - i);

  // Fetch all clients
  const { data: clients = [] } = useQuery({
    queryKey: ["clients-list"],
    queryFn: async (): Promise<Client[]> => {
      const { data, error } = await supabase
        .from("clients")
        .select("id, name")
        .order("name");
      if (error) throw error;
      return data || [];
    },
  });

  // Fetch all exploitations for the selected year (across all clients)
  const { data: exploitations = [], isLoading } = useQuery({
    queryKey: ["all-client-exploitations", selectedYear],
    queryFn: async (): Promise<ExploitationRow[]> => {
      const { data, error } = await supabase
        .from("client_exploitations")
        .select("*")
        .eq("year", selectedYear)
        .order("created_at", { ascending: true });
      if (error) throw error;
      return data || [];
    },
  });

  // Sync editableRows with fetched data
  useEffect(() => {
    setEditableRows(exploitations.map(e => ({ ...e, isNew: false, isDirty: false })));
  }, [exploitations]);

  // Create mutation
  const createMutation = useMutation({
    mutationFn: async (row: { client_id: string; description: string; product_category: string | null; month: number | null; sales: number; probability: number; year: number }) => {
      const { data, error } = await supabase
        .from("client_exploitations")
        .insert([row])
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["all-client-exploitations", selectedYear] });
      // Also invalidate client-specific exploitation query for sync with Clients tab
      queryClient.invalidateQueries({ queryKey: ["client-exploitations", data.client_id, selectedYear] });
      toast({ title: "Success", description: "Row saved successfully" });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async (row: { id: string; client_id: string; description: string; product_category: string | null; month: number | null; sales: number; probability: number }) => {
      const { data, error } = await supabase
        .from("client_exploitations")
        .update({
          client_id: row.client_id,
          description: row.description,
          product_category: row.product_category,
          month: row.month,
          sales: row.sales,
          probability: row.probability,
          updated_at: new Date().toISOString(),
        })
        .eq("id", row.id)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["all-client-exploitations", selectedYear] });
      // Also invalidate client-specific exploitation query for sync with Clients tab
      queryClient.invalidateQueries({ queryKey: ["client-exploitations", data.client_id, selectedYear] });
      toast({ title: "Success", description: "Changes saved" });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (row: { id: string; client_id: string }) => {
      const { error } = await supabase
        .from("client_exploitations")
        .delete()
        .eq("id", row.id);
      if (error) throw error;
      return row;
    },
    onSuccess: (row) => {
      queryClient.invalidateQueries({ queryKey: ["all-client-exploitations", selectedYear] });
      // Also invalidate client-specific exploitation query for sync with Clients tab
      queryClient.invalidateQueries({ queryKey: ["client-exploitations", row.client_id, selectedYear] });
      toast({ title: "Success", description: "Row deleted" });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  // Add new row
  const handleAddRow = useCallback(() => {
    const newRow: ExploitationRow = {
      id: `temp-${Date.now()}`,
      client_id: "",
      description: "",
      product_category: null,
      month: null,
      sales: 0,
      probability: 0,
      year: selectedYear,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      created_by: null,
      isNew: true,
      isDirty: true,
    };
    setEditableRows(prev => [...prev, newRow]);
  }, [selectedYear]);

  // Update local row data
  const handleRowChange = useCallback((index: number, field: keyof ExploitationRow, value: string | number) => {
    setEditableRows(prev => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        [field]: value,
        isDirty: true,
      };
      return updated;
    });
  }, []);

  // Save row
  const handleSaveRow = useCallback(async (row: ExploitationRow, index: number) => {
    if (!row.client_id) {
      toast({ title: "Error", description: "Please select a client", variant: "destructive" });
      return;
    }
    
    if (row.isNew) {
      await createMutation.mutateAsync({
        client_id: row.client_id,
        description: row.description || "",
        product_category: row.product_category,
        month: row.month,
        sales: row.sales || 0,
        probability: row.probability || 0,
        year: selectedYear,
      });
    } else {
      await updateMutation.mutateAsync({
        id: row.id,
        client_id: row.client_id,
        description: row.description || "",
        product_category: row.product_category,
        month: row.month,
        sales: row.sales || 0,
        probability: row.probability || 0,
      });
    }
    
    // Mark as not dirty after save
    setEditableRows(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], isDirty: false, isNew: false };
      return updated;
    });
  }, [selectedYear, createMutation, updateMutation, toast]);

  // Delete row
  const handleDeleteRow = useCallback(async (row: ExploitationRow, index: number) => {
    if (row.isNew) {
      setEditableRows(prev => prev.filter((_, i) => i !== index));
    } else {
      await deleteMutation.mutateAsync({ id: row.id, client_id: row.client_id });
    }
  }, [deleteMutation]);

  // Calculate estimated sales
  const calculateEstimatedSales = (sales: number, probability: number): number => {
    return Math.round(sales * (probability / 100) * 100) / 100;
  };

  // Calculate totals
  const totals = editableRows.reduce(
    (acc, row) => ({
      sales: acc.sales + (row.sales || 0),
      estimatedSales: acc.estimatedSales + calculateEstimatedSales(row.sales || 0, row.probability || 0),
    }),
    { sales: 0, estimatedSales: 0 }
  );

  // Format currency
  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat("el-GR", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  // Get client name by id
  const getClientName = (clientId: string): string => {
    return clients.find(c => c.id === clientId)?.name || "";
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <TrendingUp className="h-6 w-6" /> Clients Architecture
        </h1>
        <Select
          value={selectedYear.toString()}
          onValueChange={(value) => setSelectedYear(parseInt(value))}
        >
          <SelectTrigger className="w-[120px]">
            <SelectValue placeholder="Select year" />
          </SelectTrigger>
          <SelectContent>
            {yearOptions.map((year) => (
              <SelectItem key={year} value={year.toString()}>
                {year}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="bg-card border rounded-lg">
        {isLoading ? (
          <div className="p-6 text-sm text-muted-foreground">Loading...</div>
        ) : (
          <div className="p-4 space-y-2 overflow-x-auto">
            {/* Header row */}
            <div className="grid grid-cols-[180px_1fr_140px_100px_100px_80px_100px_80px] gap-2 text-xs font-medium text-muted-foreground border-b pb-2 min-w-[1000px]">
              <div>Client</div>
              <div>Description</div>
              <div>Product Category</div>
              <div>Month</div>
              <div className="text-right">Sales (€)</div>
              <div className="text-right">Prob. (%)</div>
              <div className="text-right">Est. Sales</div>
              <div className="text-center">Actions</div>
            </div>

            {/* Data rows */}
            {editableRows.map((row, index) => (
              <div
                key={row.id}
                className={cn(
                  "grid grid-cols-[180px_1fr_140px_100px_100px_80px_100px_80px] gap-2 items-center py-1 min-w-[1000px]",
                  row.isDirty && "bg-yellow-50 dark:bg-yellow-950/20 -mx-2 px-2 rounded"
                )}
              >
                <Select
                  value={row.client_id}
                  onValueChange={(value) => handleRowChange(index, "client_id", value)}
                >
                  <SelectTrigger className="h-8 text-sm">
                    <SelectValue placeholder="Select client..." />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map((client) => (
                      <SelectItem key={client.id} value={client.id}>
                        {client.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  value={row.description || ""}
                  onChange={(e) => handleRowChange(index, "description", e.target.value)}
                  placeholder="Enter description..."
                  className="h-8 text-sm"
                />
                <Select
                  value={row.product_category || ""}
                  onValueChange={(value) => handleRowChange(index, "product_category", value)}
                >
                  <SelectTrigger className="h-8 text-sm">
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent>
                    {PRODUCT_CATEGORIES.map((category) => (
                      <SelectItem key={category} value={category}>
                        {category}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select
                  value={row.month?.toString() || ""}
                  onValueChange={(value) => handleRowChange(index, "month", parseInt(value))}
                >
                  <SelectTrigger className="h-8 text-sm">
                    <SelectValue placeholder="Month..." />
                  </SelectTrigger>
                  <SelectContent>
                    {MONTHS.map((month) => (
                      <SelectItem key={month.value} value={month.value.toString()}>
                        {month.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  type="number"
                  value={row.sales || ""}
                  onChange={(e) => handleRowChange(index, "sales", parseFloat(e.target.value) || 0)}
                  placeholder="0"
                  className="h-8 text-sm text-right"
                  min={0}
                />
                <Input
                  type="number"
                  value={row.probability || ""}
                  onChange={(e) => {
                    const val = Math.min(100, Math.max(0, parseInt(e.target.value) || 0));
                    handleRowChange(index, "probability", val);
                  }}
                  placeholder="0"
                  className="h-8 text-sm text-right"
                  min={0}
                  max={100}
                />
                <div className="text-sm text-right font-medium text-primary">
                  {formatCurrency(calculateEstimatedSales(row.sales || 0, row.probability || 0))}
                </div>
                <div className="flex items-center justify-center gap-1">
                  {row.isDirty && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0 text-green-600 hover:text-green-700 hover:bg-green-50"
                      onClick={() => handleSaveRow(row, index)}
                      disabled={createMutation.isPending || updateMutation.isPending}
                    >
                      <Save className="h-4 w-4" />
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                    onClick={() => handleDeleteRow(row, index)}
                    disabled={deleteMutation.isPending}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}

            {/* Empty state */}
            {editableRows.length === 0 && (
              <div className="text-center py-8 text-muted-foreground text-sm">
                No exploitation data for {selectedYear}. Click + to add a row.
              </div>
            )}

            {/* Totals row */}
            {editableRows.length > 0 && (
              <div className="grid grid-cols-[180px_1fr_140px_100px_100px_80px_100px_80px] gap-2 items-center pt-3 border-t font-medium min-w-[1000px]">
                <div className="text-sm">Total</div>
                <div />
                <div />
                <div />
                <div className="text-sm text-right">{formatCurrency(totals.sales)}</div>
                <div className="text-sm text-right text-muted-foreground">—</div>
                <div className="text-sm text-right text-primary">{formatCurrency(totals.estimatedSales)}</div>
                <div />
              </div>
            )}

            {/* Add row button */}
            <div className="pt-2">
              <Button
                variant="outline"
                size="sm"
                className="w-full border-dashed"
                onClick={handleAddRow}
              >
                <Plus className="h-4 w-4 mr-1" />
                Add Row
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
